import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./InstallmentSummary.module.scss";
import {
  InstallmentBankTypes,
  InstallmentTermTypes,
} from "../../../../api/types/products.types";
import { resolveMediaUrl } from "../../../../utils/mediaUrl";

/**
 * The plan in play. It names the bank and the term only — the amounts are
 * derived from whatever the order total is at the time, so they cannot go
 * stale when a quantity changes or delivery becomes free.
 */
export interface InstallmentSelection {
  bank: InstallmentBankTypes;
  term: InstallmentTermTypes;
}

interface Props {
  banks: InstallmentBankTypes[];
  /** Order total, delivery included, in the currency being shopped in. */
  total: number;
  currency: string;
  selection: InstallmentSelection | null;
  onSelect: (selection: InstallmentSelection | null) => void;
}

/**
 * The instalment picker inside the basket's order summary.
 *
 * Unlike the product page, this one is a control rather than a note: picking a
 * bank and a term is what makes the summary quote the monthly payment instead
 * of the full total. It lives in a narrow column, so it is smaller still —
 * card art alone identifies each bank, with the name spelled out only for the
 * one in play.
 */
const InstallmentSummary = ({
  banks,
  total,
  currency,
  selection,
  onSelect,
}: Props) => {
  const { t } = useTranslation("translation");
  const [activeBankId, setActiveBankId] = useState<number | null>(null);

  if (!banks.length || !total || total <= 0) return null;

  const activeBank =
    banks.find((bank) => bank.id === (activeBankId ?? selection?.bank.id)) ??
    banks[0];

  // Switching banks carries the chosen length over where the new bank offers
  // it, so the highlighted plan and the total below never disagree.
  const selectBank = (bank: InstallmentBankTypes) => {
    setActiveBankId(bank.id);

    if (!selection || selection.bank.id === bank.id) return;

    const sameLength = bank.terms.find(
      (term) => term.months === selection.term.months,
    );
    onSelect(sameLength ? { bank, term: sameLength } : null);
  };

  const selectTerm = (
    bank: InstallmentBankTypes,
    term: InstallmentTermTypes,
  ) => {
    // Tapping the plan already in play puts the summary back on the full total.
    if (selection?.term.id === term.id) {
      onSelect(null);
      return;
    }

    onSelect({ bank, term });
  };

  return (
    <div className={styles.installments}>
      <div className={styles.header}>
        <span className={styles.title}>{t("installments.title")}</span>
        {selection && (
          <button
            type="button"
            className={styles.clear}
            onClick={() => onSelect(null)}
          >
            {t("installments.clear")}
          </button>
        )}
      </div>

      <div className={styles.banks}>
        {banks.map((bank) => {
          const image = resolveMediaUrl(bank.image);
          const isActive = bank.id === activeBank.id;

          return (
            <button
              type="button"
              key={bank.id}
              title={bank.name}
              className={`${styles.bank} ${isActive ? styles.bank_active : ""}`}
              style={isActive ? { borderColor: bank.accent_color } : undefined}
              onClick={() => selectBank(bank)}
            >
              {image ? (
                <img src={image} alt={bank.name} className={styles.card_art} />
              ) : (
                <span
                  className={styles.card_fallback}
                  style={{ background: bank.background_color }}
                >
                  <span
                    className={styles.card_stripe}
                    style={{ background: bank.accent_color }}
                  />
                </span>
              )}
            </button>
          );
        })}
      </div>

      <p className={styles.bank_name}>{activeBank.name}</p>

      <div className={styles.terms}>
        {activeBank.terms.map((term) => {
          const isSelected = selection?.term.id === term.id;
          const monthly =
            (total * (1 + term.markup_percent / 100)) / term.months;

          return (
            <button
              type="button"
              key={term.id}
              className={`${styles.term} ${isSelected ? styles.term_selected : ""}`}
              style={
                isSelected
                  ? {
                      borderColor: activeBank.accent_color,
                      background: activeBank.accent_color,
                    }
                  : undefined
              }
              onClick={() => selectTerm(activeBank, term)}
            >
              <span className={styles.term_months}>
                {t("installments.months", { months: term.months })}
              </span>
              <span className={styles.term_amount}>
                {monthly.toFixed(2)} {currency}
              </span>
              {term.markup_percent > 0 && (
                <span className={styles.term_markup}>
                  +{term.markup_percent}%
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default InstallmentSummary;
